import { RadarDistanceCirclesComponent } from './radar/radar-distance-circles.component';
import { RadarMapLayersComponent } from './radar/radar-map-layers.component';

import { PigeonsService } from '../pigeons/pigeons.service';
import { RadarDtg } from '../radar/radar.service';
import { RouteFcstService } from '../route-fcst/route-fcst.service';
import { LanguageService } from '../shared/services/language.service';
import { AddressPopup, MapService } from './map.service';

import { IsNilPipe } from '../pipes/is-nil.pipe';
import { DatexPipe } from '../shared/datex.pipe';
import { KeyPipe } from '../shared/key.pipe';
import { RoundPipe } from '../shared/round.pipe';
import { GetEopValuePipe } from './pipes/get-eop-value.pipe';
import { PigeonRoutePartColorPipe } from './pipes/pigeon-route-part-color.pipe';

import { WmIconDirective } from '../shared/wm-icon.directive';

import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  effect,
  HostListener,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { RouterModule } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import {
  ControlComponent,
  FeatureComponent,
  FullscreenControlDirective,
  GeoJSONSourceComponent,
  ImageComponent,
  ImageSourceComponent,
  LayerComponent,
  MapComponent as NgxMapboxMapComponent,
  MarkerComponent,
  NavigationControlDirective,
  PopupComponent,
  RasterSourceComponent,
} from 'ngx-mapbox-gl';

import {
  FaIconComponent,
  FaLayersComponent,
} from '@fortawesome/angular-fontawesome';

import { environment } from '../../environments/environment';

import { parameterSettings } from '../pigeons/constants/constants';
import { PigeonRoute } from '../pigeons/models/pigeon-route.interface';
import { CustomIconControl } from './custom-controls/custom-icon.control';
import {
  createRadarCirclePaint,
  createRasterInvisiblePaint,
  createRasterVisiblePaint,
  getRadarViewportState,
  RADAR_PROJECTIONS,
  RadarCirclePaint,
} from './radar/radar-map.utils';
import {
  createEmptyRadarRadiusFeatureCollection,
  createRadarRadiusFeatureCollection,
  toRadarCircleCenter,
} from './radar/radar-radius.utils';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import bbox from '@turf/bbox';
import html2canvas from 'html2canvas';
import mapboxgl, {
  BackgroundLayerSpecification,
  CircleLayerSpecification,
  LngLat,
  LngLatLike,
  Map,
  MapboxGeoJSONFeature,
  MapMouseEvent,
  Marker,
  RasterLayerSpecification,
  SymbolLayerSpecification,
} from 'mapbox-gl';
import moment, { Moment } from 'moment';
import { Subscription, tap } from 'rxjs';
import { onSelectLayer, setSavedMapLayerStyle } from 'src/utils/constants';
import { isNil } from 'src/utils/utils';

export enum MapType {
  RADAR = 'radar',
  ROUTEFCST = 'routefcst',
  MARWIS = 'marwis',
  PIGEONS = 'pigeons',
  SATELLITE = 'satellite',
}
export const MODULES = [
  CommonModule,
  MatProgressBarModule,
  RouterModule,
  MatButtonModule,
  MatIconModule,
  TranslateModule,
];
export const COMPONENTS = [
  FaLayersComponent,
  FaIconComponent,
  NgxMapboxMapComponent,
  LayerComponent,
  ControlComponent,
  RasterSourceComponent,
  GeoJSONSourceComponent,
  FeatureComponent,
  PopupComponent,
  MarkerComponent,
  ImageComponent,
  ImageSourceComponent,
  RadarMapLayersComponent,
  RadarDistanceCirclesComponent,
];
export const PIPES = [
  DatexPipe,
  RoundPipe,
  KeyPipe,
  GetEopValuePipe,
  PigeonRoutePartColorPipe,
  IsNilPipe,
];
export const DIRECTIVES = [
  WmIconDirective,
  NavigationControlDirective,
  FullscreenControlDirective,
];

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  providers: [MapService, RouteFcstService],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, ...DIRECTIVES],
})
export class MapComponent implements OnInit, OnDestroy {
  private injector = inject(Injector);
  private mapService = inject(MapService);
  public routeFcstService = inject(RouteFcstService);
  public pigeonsService = inject(PigeonsService);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  public languageService = inject(LanguageService);

  mapType = input<MapType>();
  region = input<string>();
  zoomPoint = input<any>();
  dtgs = input<Array<RadarDtg>>();
  dtgIndex = input<number>();
  showCellTracking = input<boolean>();
  loadingData = input<boolean>();
  product = input<any>();
  parameter = input<string>();
  geoJson = input<any>();
  backgroundOpacity = input<number>(0.1);
  marwisFeature = input<any>();
  pigeonRoute = input<PigeonRoute>();
  pigeonRouteDetails = input<any>();
  pigeonMos = input<{}>({});
  pigeonRoutePosition = input<any>();
  pigeonStationTime = input<any>();
  satelliteImageCoordinates =
    input<
      [[number, number], [number, number], [number, number], [number, number]]
    >();
  satelliteImageUrl = input<string>();
  pigeonShowTileAround = input<boolean>();
  pigeonModelRun = input<Moment | null>(null);
  radarCirclesPaint = input<boolean>(true);

  addressInfo = output<AddressPopup>();
  marwisClick = output<any>();
  pigeonPositionChange = output<any>();
  pigeonStartChange = output<any>();
  pigeonEndChange = output<any>();
  pigeonHourChange = output<number>();
  pigeonParameterChange = output<string>();
  mapLoad = output<mapboxgl.Map>();

  mapboxAccessToken = environment.mapbox.accessToken;
  map: Map;
  public mapLayers = environment.mapbox.layers;

  zoomPointCoordinates: LngLatLike;
  zoomLevel = signal(6);
  minMapZoom = 1; // how far you can zoom out 0 = whole world, 1 = continent ...
  maxMapZoom = 15; // how far you can zoom in
  geometry = signal<any>({
    type: 'FeatureCollection',
    features: [],
  });
  mapTypes: typeof MapType = MapType;

  circlePaint: RadarCirclePaint;
  readonly labels = signal<any[]>([]);
  radarPaintVisible: RasterLayerSpecification['paint'];
  radarPaintInvisible: RasterLayerSpecification['paint'];
  pigeonPaintVisible: RasterLayerSpecification['paint'];
  backgroundPaintStyle: BackgroundLayerSpecification['paint'];
  placesPaint: SymbolLayerSpecification['paint'];
  marwisPaint: CircleLayerSpecification['paint'];
  marwisClusterProperties = {
    road_tt: ['min', ['number', ['get', 'road_tt']]],
    tt: ['min', ['number', ['get', 'tt']]],
    td: ['min', ['number', ['get', 'td']]],
    road_condition: ['max', ['number', ['get', 'road_condition']]],
    rh: ['max', ['number', ['get', 'rh']]],
    road_rh: ['max', ['number', ['get', 'road_rh']]],
    wft: ['max', ['number', ['get', 'wft']]],
    dtg: ['string', ['get', 'dtg']],
  };

  imagesToLoad = signal(0);
  isStyleLoading = false;

  routeTile = signal('');
  stationTile = signal('');
  pigeonTilePlaces = signal('');
  pigeonTileDestination = signal('');
  pigeonTileAround = signal('');
  pigeonFlipIcon = null;
  pigeonHours = signal([]);
  pigeonParameters = parameterSettings;

  radarProjections = RADAR_PROJECTIONS;

  maxBounds: any = [-20, 28, 36, 61]; // [west, south, east, north]

  stations = [];
  bounds;
  boundsPadding = 50;

  addressPopup = signal<AddressPopup>(undefined);
  pigeonPopup = signal({});

  marwisPopup = signal<any>(undefined);

  loadedIcons = signal(0);
  fullScreenMode = signal(false);

  satellitePaintStyle;

  localStorageId = '*_map_layer';
  style = 'mapbox://styles/' + environment.mapbox.layers[0].style;
  styleName = environment.mapbox.layers[0].name;

  mapLanguageControl: MapboxLanguage;
  private languageChangeSubscription?: Subscription;
  private layerControl?: CustomIconControl;
  private saveMapControl?: CustomIconControl;
  private hasLayerControl = false;
  private hasSaveMapControl = false;
  private hasLanguageControl = false;

  constructor() {
    this.listenToPigeonRouteDetailsChanges();
    this.listenToPigeonChanges();
    this.listenToPigeonStationTimeChnages();
    this.listenToZoomPointChanges();
    this.listenToRouteFcstChanges();
    this.listenToBackgroundOpacityChanges();
    this.listenToMarwisGeoJsonChanges();
    this.listenToMarwisParameterChanges();
    this.listenToMapTypeChanges();
  }

  ngOnInit(): void {
    const mapType = this.mapType();
    const zoomPoint = this.zoomPoint();
    const dtgs = this.dtgs();
    const dtgIndex = this.dtgIndex();
    const parameter = this.parameter();

    if (mapType === MapType.MARWIS) {
      this.zoomPointCoordinates = [9.8, 51.5];
      this.setMarwisPaintStyle();
      this.maxMapZoom = 16;
    } else if (mapType === MapType.RADAR) {
      this.initializeRadarViewport();
      this.setRadarPaintStyle();
    } else if (mapType !== MapType.PIGEONS) {
      const viewportState = getRadarViewportState(this.region(), zoomPoint);

      this.zoomPointCoordinates = viewportState.center;
      this.zoomLevel.set(viewportState.zoomLevel);
      this.setRadarPaintStyle();
    }

    if (mapType === MapType.RADAR && this.radarCirclesPaint()) {
      setTimeout(() => {
        this.paintCircles();
      }, 1000);
    } else if (mapType === MapType.ROUTEFCST) {
      this.extractStations();
      if (dtgs.length > dtgIndex) {
        this.routeTile.set(this.getRouteTileUrl(dtgs[dtgIndex], parameter));
        if (this.stations.length > 0) {
          this.stationTile.set(this.getStationValuesUrl(dtgs[dtgIndex]));
        }
      }
    } else if (mapType === MapType.PIGEONS) {
      this.boundsPadding = 75;
      this.pigeonTilePlaces.set(this.getPigeonTilePlacesUrl());
      this.pigeonTileDestination.set(this.getPigeonTileDestinationUrl());
      this.pigeonTileAround.set(
        this.getPigeonTileAroundUrl(dtgs[dtgIndex].mapServer, parameter)
      );
    }
    this.setBackgroundPaintStyle();
    if (mapType !== MapType.RADAR) {
      this.setBounds();
      // this.map.fitBounds(this.bounds, {padding: 50});
    }
    if (mapType === MapType.SATELLITE) {
      this.initializeSatelliteMap(zoomPoint);
    }
  }

  increaseLoadedIcons() {
    this.loadedIcons.update(loadedIcons => loadedIcons + 1);
  }

  private initializeRadarViewport(): void {
    const viewportState = getRadarViewportState(
      this.region(),
      this.zoomPoint()
    );

    this.zoomPointCoordinates = viewportState.center;
    this.zoomLevel.set(viewportState.zoomLevel);
  }

  private updateRadarViewportAndCircles(): void {
    this.geometry.set(createEmptyRadarRadiusFeatureCollection());
    this.initializeRadarViewport();

    if (this.radarCirclesPaint()) {
      setTimeout(() => {
        this.paintCircles();
      }, 1000);
    }
  }

  doesSourceExist(id: string): boolean {
    if (this.map) {
      if (this.map.getSource(id)) {
        return true;
      }
    }
    return false;
  }

  async updatePigeonRouteDetails(): Promise<void> {
    const pigeonRouteDetails = this.pigeonRouteDetails();
    this.pigeonFlipIcon =
      pigeonRouteDetails.start.lng < pigeonRouteDetails.end.lng
        ? null
        : 'horizontal';
    this.bounds = [
      Math.min(pigeonRouteDetails.start.lng, pigeonRouteDetails.end.lng),
      Math.min(pigeonRouteDetails.start.lat, pigeonRouteDetails.end.lat),
      Math.max(pigeonRouteDetails.start.lng, pigeonRouteDetails.end.lng),
      Math.max(pigeonRouteDetails.start.lat, pigeonRouteDetails.end.lat),
    ];
    if (this.map) {
      this.map.fitBounds(this.bounds, {
        padding: {
          top: this.boundsPadding,
          bottom: this.boundsPadding,
          left: this.boundsPadding,
          right: this.boundsPadding,
        },
      });
    }
  }

  setBounds(): void {
    const mapType = this.mapType();
    if (mapType === MapType.ROUTEFCST) {
      const routeRegion = this.product()['zoomRegions']['roads'][0];
      this.bounds = [
        routeRegion['southWestBBoxCoordinate']['longitude'],
        routeRegion['southWestBBoxCoordinate']['latitude'],
        routeRegion['northEastBBoxCoordinate']['longitude'],
        routeRegion['northEastBBoxCoordinate']['latitude'],
      ];
      this.maxBounds = [
        routeRegion['southWestBBoxCoordinate']['longitude'] - 0.1,
        routeRegion['southWestBBoxCoordinate']['latitude'] - 0.1,
        routeRegion['northEastBBoxCoordinate']['longitude'] + 0.1,
        routeRegion['northEastBBoxCoordinate']['latitude'] + 0.1,
      ];
      this.zoomLevel.set(routeRegion['zoomLevel']);
    } else if (mapType === MapType.MARWIS) {
      this.setMarwisBounds();
    }
  }

  setMarwisBounds(): void {
    const geoJson = this.geoJson();
    if (
      geoJson &&
      geoJson.hasOwnProperty('features') &&
      geoJson.features.length > 0
    ) {
      this.bounds = bbox(geoJson);
      if (this.map) {
        this.map.fitBounds(this.bounds, {
          padding: {
            top: this.boundsPadding,
            bottom: this.boundsPadding,
            left: this.boundsPadding,
            right: this.boundsPadding,
          },
        });
      }
    } else {
      this.bounds = this.radarProjections['de'];
    }
  }

  extractStations(): void {
    this.stations = [];
    for (const station of this.product()['stations']) {
      this.stations.push(station['stationId']);
    }
  }

  setBackgroundPaintStyle(): void {
    this.backgroundPaintStyle = {
      'background-color': 'black',
      'background-opacity': this.backgroundOpacity(),
    };
    this.placesPaint = {
      'text-color': 'white',
      'text-opacity': 1,
    };
  }

  setRadarPaintStyle(): void {
    this.radarPaintInvisible = createRasterInvisiblePaint();
    this.radarPaintVisible = createRasterVisiblePaint();
    this.pigeonPaintVisible = createRasterVisiblePaint();
    this.circlePaint = createRadarCirclePaint(this.styleName);
  }

  setMarwisPaintStyle(): void {
    const colorMapping = {
      tt: [
        [-4, '#000000'],
        [-3, '#0000FF'],
        [-3, '#007FFF'],
        [-2, '#007FFF'],
        [-1.25, '#3EC0FF'],
        [-0.75, '#00AE00'],
        [-0.25, '#00FF00'],
        [0.25, '#D0D0D2'],
        [0.75, '#FFFF00'],
        [1.25, '#FF7F00'],
        [2, '#FF0000'],
        [3, '#C80032'],
        [3, '#C80032'],
        [3.1, '#960096'],
      ],
      td: [
        [-4, '#000000'],
        [-3, '#0000FF'],
        [-3, '#007FFF'],
        [-2, '#007FFF'],
        [-1.25, '#3EC0FF'],
        [-0.75, '#00AE00'],
        [-0.25, '#00FF00'],
        [0.25, '#D0D0D2'],
        [0.75, '#FFFF00'],
        [1.25, '#FF7F00'],
        [2, '#FF0000'],
        [3, '#C80032'],
        [3, '#C80032'],
        [3.1, '#960096'],
      ],
      road_tt: [
        [-4, '#000000'],
        [-3, '#0000FF'],
        [-3, '#007FFF'],
        [-2, '#007FFF'],
        [-1.25, '#3EC0FF'],
        [-0.75, '#00AE00'],
        [-0.25, '#00FF00'],
        [0.25, '#D0D0D2'],
        [0.75, '#FFFF00'],
        [1.25, '#FF7F00'],
        [2, '#FF0000'],
        [3, '#C80032'],
        [3, '#C80032'],
        [3.1, '#960096'],
      ],
      road_condition: [
        [0, '#00FF00'],
        [1, '#0000FF'],
        [2, '#0000FF'],
        [3, '#FF0000'],
        [4, '#FF0000'],
        [5, '#0000FF'],
        [6, '#FF0000'],
        [7, '#FF0000'],
        [8, '#FF0000'],
      ],
      rh: [
        [10, '#A26B3C'],
        [20, '#B77E53'],
        [30, '#CC9B7A'],
        [40, '#D8AF97'],
        [50, '#CCFDFF'],
        [60, '#99F8FF'],
        [70, '#65EFFF'],
        [80, '#32E3FF'],
        [90, '#00BFE9'],
        [100, '#A26B3C'],
      ],
      road_rh: [
        [10, '#A26B3C'],
        [20, '#B77E53'],
        [30, '#CC9B7A'],
        [40, '#D8AF97'],
        [50, '#CCFDFF'],
        [60, '#99F8FF'],
        [70, '#65EFFF'],
        [80, '#32E3FF'],
        [90, '#00BFE9'],
        [100, '#A26B3C'],
      ],
      wft: [
        [10, '#008000'],
        [100, '#87CEFA'],
        [1000, '#00BFFF'],
        [10000, '#DC143C'],
      ],
    };
    const parameter = this.parameter();
    this.marwisPaint = {
      'circle-color': {
        property: parameter,
        stops: colorMapping[parameter],
      },
      'circle-radius': 5,
      'circle-opacity': 0.9,
    };
  }

  /*private ensureRadiusLayers(): void {
    if (!this.map) {
      return;
    }

    if (!this.map.getSource('radius-source')) {
      this.map.addSource('radius-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });
    }

    if (!this.map.getLayer('radius-lines')) {
      this.map.addLayer({
        id: 'radius-lines',
        type: 'line',
        source: 'radius-source',
        filter: ['==', ['get', 'type'], 'outline'],
        paint: {
          'line-color':
            this.styleName === 'street' ? 'rgba(88, 88, 88, 1)' : 'white',
          'line-width': 1,
          'line-dasharray': [2, 2],
        },
      });
    }

    if (!this.map.getLayer('radius-labels')) {
      this.map.addLayer({
        id: 'radius-labels',
        type: 'symbol',
        source: 'radius-source',
        filter: ['==', ['get', 'type'], 'label'],
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 12,
          'text-offset': [0, -1],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        },
        paint: {
          'text-color':
            this.styleName === 'street' ? 'rgb(88, 88, 88)' : 'white',
          'text-halo-color':
            this.styleName === 'street'
              ? 'rgba(255,255,255,0.5)'
              : 'rgba(0,0,0,0.5)',
          'text-halo-width': 1,
        },
      });
    }
  }*/

  paintCircles(): void {
    if (this.zoomLevel() <= 6 || !this.zoomPointCoordinates) {
      this.removeCircles();
      return;
    }

    const center = toRadarCircleCenter(this.zoomPointCoordinates);

    if (!center) {
      this.removeCircles();
      return;
    }

    this.geometry.set(createRadarRadiusFeatureCollection(center));
  }

  setCircleStyle(): void {
    this.circlePaint = createRadarCirclePaint(this.styleName);
  }

  removeCircles(): void {
    this.geometry.set(createEmptyRadarRadiusFeatureCollection());
  }

  public setActiveMapLayer(inputMapLayer: {
    name: string;
    style: string;
  }): void {
    const mapType = this.mapType();

    this.styleName = inputMapLayer.name;

    if (mapType === MapType.RADAR) {
      this.setCircleStyle();

      if (this.radarCirclesPaint()) {
        this.paintCircles();
      }

      return;
    }

    if (mapType === MapType.ROUTEFCST) {
      this.routeTile.set(null);
      this.stationTile.set(null);

      const dtgs = this.dtgs();
      const dtgIndex = this.dtgIndex();

      if (!dtgs?.length || isNil(dtgIndex) || !dtgs[dtgIndex]) {
        return;
      }

      this.routeTile.set(
        this.getRouteTileUrl(dtgs[dtgIndex], this.parameter())
      );

      if (this.stations.length > 0) {
        this.stationTile.set(this.getStationValuesUrl(dtgs[dtgIndex]));
      }
    }
  }

  // https://mapserver.metops.net/fcgi-bin/mapserv?map=/srv/mapserver/maps/radar/radar_map.map&service=WMS&request=GetMap&
  // layers=PRECIPITATIONEU&styles=&format=image%2Fpng&transparent=true&version=1.3.0&TIME=202210130400&width=1024&height=1024&
  // crs=EPSG%3A3857&bbox=0,6261721.357121641,1252344.2714243277,7514065.628545967

  getPigeonTilePlacesUrl(): string {
    const url =
      environment.url.mapServer +
      '/fcgi-bin/mapserv?map=/srv/mapserver/maps/tauben/tauben_map.map' +
      '&service=WMS&request=GetMap&layers=TAUBENAUFLASSORTE' +
      '&format=image%2Fpng&transparent=true&version=1.3.0&width=1024&height=1024&crs=EPSG%3A3857&bbox={bbox-epsg-3857}';
    return url;
  }

  getPigeonTileDestinationUrl(): string {
    const url =
      environment.url.mapServer +
      '/fcgi-bin/mapserv?map=/srv/mapserver/maps/tauben/tauben_map.map' +
      '&service=WMS&request=GetMap&layers=TAUBENZIELORTE' +
      '&format=image%2Fpng&transparent=true&version=1.3.0&width=1024&height=1024&crs=EPSG%3A3857&bbox={bbox-epsg-3857}';
    return url;
  }

  getPigeonTileAroundUrl(time: string, parameter: string): string {
    const pigeonModelRun = this.pigeonModelRun();
    const modelRun =
      pigeonModelRun === null
        ? ''
        : '&RUN=' + pigeonModelRun.utc().toISOString();
    const endpoint =
      pigeonModelRun === null ? 'pigeon_map.map' : 'pigeon_archive.map';
    const url =
      environment.url.mapServer +
      '/fcgi-bin/mapserv?map=/srv/mapserver/maps/tauben/' +
      endpoint +
      '&service=WMS&request=GetMap&layers=PIGEON' +
      parameter +
      '&TIME=' +
      time +
      modelRun +
      '&format=image%2Fpng&transparent=true&version=1.3.0&width=512&height=512&crs=EPSG%3A3857&bbox={bbox-epsg-3857}';
    return url;
  }

  // https://mapserver.metops.net/fcgi-bin/mapserv?map=/srv/mapserver/maps/tauben/pigeon_map.map&service=WMS&request=GetMap&layers=PIGEON&format=image%2Fpng&transparent=false&version=1.3.0&TIME=2022-03-29%2006%3A00%3A00&width=1024&height=1024&crs=EPSG%3A3857&bbox=0,5009377.085697314,2504688.5428486555,7514065.628545967

  getRouteTileUrl(dtg: RadarDtg, mapParameter: string): string {
    const roadType = 'customer';
    const product = this.product();
    const url =
      environment.url.mapServer +
      '/fcgi-bin/mapserv?map=/srv/mapserver/maps/route/route_map' +
      product.id +
      '.map' +
      '&service=WMS&request=GetMap&TYPE=' +
      roadType +
      '&PRODUCT=' +
      product.id +
      '&PARAM=' +
      mapParameter +
      '&RUN=' +
      this.dtgs()[0].mapServer +
      '&DTG=' +
      dtg.mapServer +
      '&layers=ulayer' +
      product.id +
      '101' +
      '&styles=&format=image%2Fpng&transparent=true&version=1.3.0&width=1024&height=1024&crs=EPSG%3A3857&bbox={bbox-epsg-3857}';
    return url;
  }

  getRouteTileUrlForTest(dtg: RadarDtg, mapParameter: string): string {
    const roadType = 'customer';
    const productId = 3978;
    const customerId = 10010;
    const url =
      environment.url.mapServer +
      '/fcgi-bin/mapserv?map=/srv/mapserver/maps/route/route_map' +
      productId +
      '.map' +
      '&service=WMS&request=GetMap&TYPE=' +
      roadType +
      '&PRODUCT=' +
      productId +
      '&PARAM=' +
      mapParameter +
      '&RUN=' +
      this.dtgs()[0].mapServer +
      '&DTG=' +
      dtg.mapServer +
      '&layers=ulayer' +
      this.product().id +
      '101' +
      '&styles=&format=image%2Fpng&transparent=true&version=1.3.0&width=1024&height=1024&crs=EPSG%3A3857&bbox={bbox-epsg-3857}';
    return url;
  }

  getStationValuesUrl(dtg: RadarDtg): string {
    const formattedDtg =
      moment.unix(dtg.slider).utc().format('YYYY-MM-DD HH') + ':00:00';
    const url =
      environment.url.mapServer +
      '/fcgi-bin/mapserv?map=/srv/mapserver/maps/route/routestation_map.map' +
      '&service=WMS&request=GetMap&layers=STATIONMOS' +
      '&PARAM=rst&COLORSCALE=TT&TIME=' +
      formattedDtg +
      '&STATIONS=' +
      this.stations.join('%2C') +
      '&styles=&format=image%2Fpng&transparent=true&version=1.3.0&width=1024&height=1024&crs=EPSG%3A3857&bbox={bbox-epsg-3857}';
    return url;
  }

  shouldIPreloadImage(imageIndex: number): boolean {
    return true;
    const difference = Math.abs(imageIndex - this.dtgIndex());
    if (difference > this.dtgs().length - 3) {
      return true;
    }
    return difference <= 3;
  }

  onMapLoad(event: { type: any; target: Map }): void {
    const loadedMap = event.target;

    this.map = loadedMap;
    this.mapLoad.emit(loadedMap);

    // Kompakte Attribution-Control statt der Standard-Variante. Der
    // "Improve this map"-Link kommt aus der Style-Quelle selbst (lässt sich
    // hier nicht unterdrücken) und wird stattdessen per CSS ausgeblendet,
    // siehe .mapbox-improve-map in map.component.scss.
    loadedMap.addControl(new mapboxgl.AttributionControl({ compact: true }));

    if (this.bounds) {
      loadedMap.fitBounds(this.bounds, {
        padding: {
          top: this.boundsPadding,
          bottom: this.boundsPadding,
          left: this.boundsPadding,
          right: this.boundsPadding,
        },
      });
    }

    if (this.mapType() === MapType.ROUTEFCST) {
      this.addSaveMapControl();
    }

    this.setLanguageControl();
  }

  onMapCreate(map: Map): void {
    this.map = map;
    this.setSelectLayerControl();
  }

  @HostListener('fullscreenchange', ['$event'])
  @HostListener('webkitfullscreenchange', ['$event'])
  @HostListener('mozfullscreenchange', ['$event'])
  @HostListener('MSFullscreenChange', ['$event'])
  screenChange(event): void {
    this.fullScreenMode.update(fullScreenMode => {
      return !fullScreenMode;
    });
    this.map.resize();
  }

  onMapClick(event: MapMouseEvent): void {
    const mapType = this.mapType();
    if (mapType === MapType.PIGEONS) {
      this.onPigeonMapClick(event);
    } else {
      this.addressPopup.set({
        position: null,
        address: null,
        street: null,
        eopData: null,
      });
      if (mapType === MapType.ROUTEFCST && event.lngLat.lng) {
        const pos: LngLatLike = [event.lngLat.lng, event.lngLat.lat];
        this.mapService.getAddressOfPosition(pos).subscribe(
          data => {
            if (
              data.hasOwnProperty('features') &&
              data['features'].length > 0
            ) {
              this.addressPopup.update(addressPopup => {
                return {
                  ...addressPopup,
                  position: pos,
                  address: data['features'][0]['place_name'],
                  street: data['features'][0]['text'],
                };
              });
            }
          },
          error =>
            console.log('Error Map onMapClick getAddressOfPosition', error)
        );
      }
    }
  }

  onMarwisClick(
    event: MapMouseEvent & { features?: MapboxGeoJSONFeature[] | undefined }
  ): void {
    this.marwisClick.emit(event.features[0]);
  }

  onMarwisMouseEnter(
    event: MapMouseEvent & { features?: MapboxGeoJSONFeature[] | undefined }
  ): void {
    if (this.mapType() === MapType.MARWIS) {
      const pos: LngLatLike = [event.lngLat.lng, event.lngLat.lat];
      if (event.features.length > 0) {
        const dataset = event.features[0].properties;
        this.marwisPopup.set({
          position: pos,
          dtg: dataset.dtg,
          value: dataset[this.parameter()],
        });
      }
    }
  }

  onMarwisMouseLeave(event): void {
    setTimeout(() => {
      this.marwisPopup.set(null);
    }, 1000);
  }

  onSaveMap() {
    const mapType = this.mapType();
    const mapContainer = document.querySelector(`#${mapType}`);

    if (!isNil(mapContainer)) {
      html2canvas(mapContainer as HTMLElement).then(canvas => {
        const dataUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `${mapType}${moment().toISOString()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    }
  }

  onPigeonPositionChange(marker: Marker): void {
    const markerPos = marker.getLngLat();
    let minDistance = 9999999;
    let targetPos = -1;
    const pigeonRouteDetails = this.pigeonRouteDetails();
    for (let i = 0; i < pigeonRouteDetails.points.length; i++) {
      const pointPos = pigeonRouteDetails.points[i].to;
      const distance = markerPos.distanceTo(
        new LngLat(pointPos.lng, pointPos.lat)
      );
      if (distance < minDistance) {
        targetPos = i;
        minDistance = distance;
      }
    }
    this.pigeonPositionChange.emit({
      posType: 'pos',
      position: targetPos,
    });
  }

  onPigeonMapClick(event: MapMouseEvent): void {
    this.pigeonPopup.set({});
    this.pigeonsService
      .findAuflassortAroundPosition(event.lngLat)
      .then(data => {
        if (data) {
          this.pigeonPopup.set({ ...data });
        }
      });
  }

  onPigeonNewRouteStart(routeStartData: any): void {
    this.pigeonPopup.set({});
    this.pigeonStartChange.emit(routeStartData);
  }

  onPigeonNewRouteEnd(routeEndData: any): void {
    this.pigeonPopup.set({});
    this.pigeonEndChange.emit(routeEndData);
  }

  onPigeonHourChange(hour: number): void {
    this.pigeonHourChange.emit(hour);
  }

  onPigeonParameterChange(parameter: string): void {
    this.pigeonParameterChange.emit(parameter);
  }

  setSatellitePaintStyle(): any {
    return {
      'raster-opacity': 0.8,
      'raster-fade-duration': 0,
      'raster-opacity-transition': { delay: 0, duration: 0 },
    };
  }

  initializeSatelliteMap(zoomPoint: {
    longitude: number;
    latitude: number;
  }): void {
    this.styleName =
      this.mapLayers.length > 0 ? this.mapLayers[0].name : this.styleName; // set layer for satellite not needed anymore when selection of mapLayer works for satellite
    this.satellitePaintStyle = this.setSatellitePaintStyle();
    this.zoomLevel.set(4);
    this.maxBounds = [-20, 38.704987, 30, 60];
    this.boundsPadding = 20;
    this.zoomPointCoordinates = [zoomPoint?.longitude, zoomPoint?.latitude];
  }

  private setSelectLayerControl(): void {
    if (!this.map || this.hasLayerControl) {
      return;
    }

    this.layerControl = new CustomIconControl(
      ['far', 'layer-group'],
      'click',
      onSelectLayer.bind(
        this,
        this.dialog,
        this.map,
        this.localStorageId,
        () => {},
        (mapLayer: any) => {
          this.setActiveMapLayer(mapLayer);
          this.setMapLanguage();
        }
      )
    );

    this.map.addControl(this.layerControl);
    this.hasLayerControl = true;
  }

  private addSaveMapControl(): void {
    if (!this.map || this.hasSaveMapControl) {
      return;
    }

    this.saveMapControl = new CustomIconControl(
      ['far', 'download'],
      'click',
      this.onSaveMap.bind(this)
    );

    this.map.addControl(this.saveMapControl);
    this.hasSaveMapControl = true;
  }

  private listenToPigeonRouteDetailsChanges(): void {
    effect(
      () => {
        const pigeonRouteDetails = this.pigeonRouteDetails();

        untracked(() => {
          if (this.mapType() !== MapType.PIGEONS) {
            return;
          }

          if (
            !pigeonRouteDetails?.start ||
            pigeonRouteDetails.start.lat === 0
          ) {
            return;
          }

          this.updatePigeonRouteDetails();
        });
      },
      { injector: this.injector }
    );
  }

  private listenToPigeonChanges(): void {
    effect(
      () => {
        const dtgs = this.dtgs();
        const dtgIndex = this.dtgIndex();
        const parameter = this.parameter();
        this.pigeonRoutePosition();

        untracked(() => {
          if (this.mapType() !== MapType.PIGEONS) {
            return;
          }

          if (
            !dtgs?.length ||
            isNil(dtgIndex) ||
            !dtgs[dtgIndex] ||
            !parameter
          ) {
            return;
          }

          this.pigeonTileAround.set(
            this.getPigeonTileAroundUrl(dtgs[dtgIndex].mapServer, parameter)
          );
        });
      },
      { injector: this.injector }
    );
  }

  private listenToPigeonStationTimeChnages(): void {
    effect(
      () => {
        const pigeonStationTime = this.pigeonStationTime();

        untracked(() => {
          if (this.mapType() !== MapType.PIGEONS || !pigeonStationTime) {
            return;
          }

          this.pigeonHours.set([
            ...Object.values(pigeonStationTime).sort((a, b) =>
              a > b ? 1 : -1
            ),
          ]);
        });
      },
      { injector: this.injector }
    );
  }

  private listenToZoomPointChanges() {
    effect(
      () => {
        this.zoomPoint();
        this.region();

        untracked(() => {
          if (this.mapType() === MapType.RADAR) {
            this.updateRadarViewportAndCircles();
          }
        });
      },
      { injector: this.injector }
    );
  }

  private listenToRouteFcstChanges(): void {
    effect(
      () => {
        const dtgs = this.dtgs();
        const dtgIndex = this.dtgIndex();
        const parameter = this.parameter();

        untracked(() => {
          if (this.mapType() !== MapType.ROUTEFCST) {
            return;
          }

          if (
            !dtgs?.length ||
            isNil(dtgIndex) ||
            !dtgs[dtgIndex] ||
            !parameter
          ) {
            return;
          }

          this.imagesToLoad.update(imagesToLoad => imagesToLoad + 1);

          this.routeTile.set(this.getRouteTileUrl(dtgs[dtgIndex], parameter));

          if (this.stations.length > 0) {
            this.stationTile.set(this.getStationValuesUrl(dtgs[dtgIndex]));
          }

          setTimeout(() => {
            this.imagesToLoad.set(0);
          }, 1000);
        });
      },
      { injector: this.injector }
    );
  }

  private listenToBackgroundOpacityChanges() {
    effect(
      () => {
        this.backgroundOpacity();

        untracked(() => {
          this.setBackgroundPaintStyle();
        });
      },
      { injector: this.injector }
    );
  }

  private listenToMarwisGeoJsonChanges() {
    effect(
      () => {
        this.geoJson();

        untracked(() => {
          if (this.mapType() === MapType.MARWIS) {
            this.setMarwisBounds();
          }
        });
      },
      { injector: this.injector }
    );
  }

  private listenToMarwisParameterChanges() {
    effect(
      () => {
        this.parameter();

        untracked(() => {
          if (this.mapType() === MapType.MARWIS) {
            this.setMarwisPaintStyle();
          }
        });
      },
      { injector: this.injector }
    );
  }

  private listenToMapTypeChanges() {
    effect(
      () => {
        const mapType = this.mapType();
        untracked(() => {
          this.localStorageId = `${mapType}_map_layer`;
          setSavedMapLayerStyle.bind(
            this,
            this.localStorageId,
            this.mapLayers,
            (style: string) => {
              this.style = style;
            }
          )();
        });
      },
      { injector: this.injector }
    );
  }

  private setLanguageControl(): void {
    if (!this.map || this.hasLanguageControl) {
      return;
    }

    this.mapLanguageControl = new MapboxLanguage();
    this.setMapLanguage();
    this.map.addControl(this.mapLanguageControl);

    this.hasLanguageControl = true;
    this.listenToLanguageChanges();
  }

  private listenToLanguageChanges(): void {
    if (this.languageChangeSubscription) {
      return;
    }

    this.languageChangeSubscription = this.translate.onLangChange
      .pipe(
        tap(event => {
          try {
            const currentStyle = this.map?.getStyle();
            if (!this.map || !currentStyle || !this.mapLanguageControl) {
              return;
            }

            const mapLanguage = this.getMapLanguage(event.lang);
            const newStyle = this.mapLanguageControl.setLanguage(
              currentStyle,
              mapLanguage
            );

            this.map.setStyle(newStyle);
          } catch (err) {
            console.error('Error applying map language', err);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private setMapLanguage() {
    const mapLanguage = this.getMapLanguage(this.translate.getCurrentLang());
    const newStyle = this.mapLanguageControl.setLanguage(
      this.map.getStyle(),
      mapLanguage
    );
    this.map.setStyle(newStyle);
  }

  private getMapLanguage(currentLanguage: string) {
    let language = currentLanguage;

    switch (currentLanguage) {
      case 'us':
        language = 'en';
        break;
      case 'nl':
        language = 'de';
        break;
      case 'es-ct':
        language = 'es';
        break;
    }
    return language;
  }

  protected readonly MapType = MapType;

  private safeRemoveControl(control: mapboxgl.IControl): void {
    try {
      this.map?.removeControl(control);
    } catch {
      // Mapbox throws if the control was already removed.
      // Ignore because component teardown should stay resilient.
    }
  }

  ngOnDestroy(): void {
    this.languageChangeSubscription?.unsubscribe();

    if (this.map) {
      if (this.layerControl && this.hasLayerControl) {
        this.safeRemoveControl(this.layerControl);
      }

      if (this.saveMapControl && this.hasSaveMapControl) {
        this.safeRemoveControl(this.saveMapControl);
      }

      if (this.mapLanguageControl && this.hasLanguageControl) {
        this.safeRemoveControl(this.mapLanguageControl);
      }
    }

    this.map = undefined;
  }
}
